// @ts-nocheck
import React, { useEffect, useState } from "react";
import { apiFetchInit } from "@/legacy/helpersModule";
import { Modal } from "@/shared/components/ui/Modal";
import { Button, Skeleton, useToast } from "@/shared/components/ui";

const EXPORT_OPTIONS = [
  {
    source: "recruitment",
    label: "Recruitment CVs",
    countKey: "recruitmentCvs",
    filenamePrefix: "cvs-recruitment",
  },
  {
    source: "talent_pool",
    label: "Talent pool CVs",
    countKey: "talentPoolCvs",
    filenamePrefix: "cvs-talent-pool",
  },
  {
    source: "registered_without_cv",
    label: "Registered, no CV",
    countKey: "registeredWithoutCv",
    filenamePrefix: "cvs-registered-without-cv",
  },
];

async function downloadCvsExport(source, filenamePrefix) {
  const q = new URLSearchParams({ source });
  const r = await fetch(
    `/api/admin/cv-analyser/inventory/export?${q}`,
    apiFetchInit(),
  );
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || "export");
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().slice(0, 10);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filenamePrefix}-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function CvsStatsModal({ onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [choosingExport, setChoosingExport] = useState(false);
  const [stats, setStats] = useState({
    recruitmentCvs: 0,
    talentPoolCvs: 0,
    totalAppCvs: 0,
    registered: 0,
    registeredWithoutCv: 0,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const r = await fetch("/api/admin/cv-analyser/stats", apiFetchInit());
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || "Failed to load CV counts");
        if (cancelled) return;
        setStats({
          recruitmentCvs: Number(data.recruitmentCvs) || 0,
          talentPoolCvs: Number(data.talentPoolCvs) || 0,
          totalAppCvs: Number(data.totalAppCvs) || 0,
          registered: Number(data.registered) || 0,
          registeredWithoutCv: Number(data.registeredWithoutCv) || 0,
        });
      } catch (e) {
        if (!cancelled) {
          setError(String(e.message || e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cards = [
    {
      label: "Recruitment CVs",
      value: stats.recruitmentCvs,
      hint: "Files stored for applied/registered candidates",
    },
    {
      label: "Talent pool CVs",
      value: stats.talentPoolCvs,
      hint: "Files from talent pool submissions",
    },
    {
      label: "Total app CVs",
      value: stats.totalAppCvs,
      hint: "Recruitment + talent pool CV files",
    },
    {
      label: "Registered without CV",
      value: stats.registeredWithoutCv,
      hint:
        stats.registered > 0
          ? `Of ${stats.registered} Registered on dashboard`
          : "Registered on dashboard with no CV file",
    },
  ];

  return (
    <Modal title="CVS — CV counts" onClose={onClose} wide>
      {error ? (
        <p className="text-sm text-red-600 mb-4">{error}</p>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {cards.map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-line bg-surface-alt px-4 py-3"
          >
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              {card.label}
            </span>
            {loading ? (
              <Skeleton className="mt-2 h-8 w-16" />
            ) : (
              <span className="block mt-1 text-xl sm:text-3xl font-bold leading-none text-ink-900">
                {card.value}
              </span>
            )}
            <span className="block mt-2 text-xs text-ink-400">{card.hint}</span>
          </div>
        ))}
      </div>

      {!choosingExport ? (
        <Button
          type="button"
          variant="secondary"
          className="w-full sm:w-auto"
          disabled={loading || exporting}
          onClick={() => setChoosingExport(true)}
        >
          Export CSV
        </Button>
      ) : (
        <div className="rounded-xl border border-line bg-white p-4">
          <p className="text-sm font-medium text-ink-900 mb-3">
            Export which list?
          </p>
          <div className="flex flex-col gap-2">
            {EXPORT_OPTIONS.map((opt) => (
              <Button
                key={opt.source}
                type="button"
                variant="secondary"
                className="w-full"
                disabled={loading || exporting}
                loading={exporting}
                onClick={async () => {
                  setExporting(true);
                  try {
                    await downloadCvsExport(opt.source, opt.filenamePrefix);
                    toast.show({
                      tone: "success",
                      title: "Export complete",
                      message: `${opt.label} CSV downloaded.`,
                    });
                    setChoosingExport(false);
                  } catch (e) {
                    toast.show({
                      tone: "danger",
                      title: "Export failed",
                      message:
                        String(e.message || e) || "Could not export CSV.",
                    });
                  } finally {
                    setExporting(false);
                  }
                }}
              >
                {opt.label}
                {!loading ? ` (${stats[opt.countKey] ?? 0})` : ""}
              </Button>
            ))}
            <Button
              type="button"
              variant="ghost"
              className="w-full sm:w-auto"
              disabled={exporting}
              onClick={() => setChoosingExport(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
